<script setup lang="ts">
import {
    CashIcon,
    ExternalLinkIcon,
    IdentificationIcon,
    LocationMarkerIcon,
    ReceiptTaxIcon,
    StarIcon,
} from '@heroicons/vue/solid'
import { computed, onBeforeMount, reactive, ref, watch } from 'vue'
import { usePersonalAccountDataState } from '../../../../utilities/account/useAccountDataPersonal'
import { kycProviderExternal } from '../../../../utilities/helpers'
import { isRequiredNaturalProfileDocuments, isVisibleNaturalProfileDocuments } from '../../../../validation'
import { i18n } from '../../../../localization'
import AccountKycProcessTabs, { KycDocumentTabs } from '../../common/verification/AccountKycProcessTabs.vue'
import {
    AccountNavigationStore,
    useAccountNavigationState,
} from '../../../../utilities/account/useAccountNavigationPersonal'
import BaseTable, { DataTableHeader } from '../../../base/table/BaseTable.vue'
import BankAccountStatusIndicator from '../../common/statusIndicator/BankAccountStatusIndicator.vue'
import EmptyState from '../../../base/EmptyState.vue'
import { FrontendBankAccountResource, putBankAccountUpdate } from '../../../../utilities/api'
import { notifyError, notifySuccess } from '../../../../utilities/useNotifications'
import { BankAccountStatus } from '../../../../typings'
import AccountKycProcessExternal from './AccountKycProcessExternal.vue'

const store = usePersonalAccountDataState()
const navigationStore = useAccountNavigationState() as AccountNavigationStore
const { naturalProfile, kycDocuments: accountKycDocuments } = store

onBeforeMount(() => {
    setKycDocs()
})

watch(accountKycDocuments, () => {
    setKycDocs()
})

function setKycDocs() {
    Object.keys(kycDocuments).forEach((key) => {
        if (kycDocuments.hasOwnProperty(key)) {
            kycDocuments[key].documents = []
        }
    })
    accountKycDocuments.forEach((kycD) => {
        if (kycDocuments.hasOwnProperty(kycD.verification_type)) {
            kycDocuments[kycD.verification_type].documents.push(kycD)
        }
    })
}

const kycDocuments = reactive({
    kyc_external: {
        header: i18n.global.t('kyc_provider.' + store.naturalProfile.platform_kyc_provider),
        icon: ExternalLinkIcon,
        accepted: computed(() => naturalProfile.kyc_id),
        required: isRequiredNaturalProfileDocuments('identity'),
        documents: [],
        show: kycProviderExternal.value,
    },
    kyc_id: {
        header: i18n.global.t('verification.identity_verification'),
        icon: IdentificationIcon,
        accepted: computed(() => naturalProfile.kyc_id),
        required: isRequiredNaturalProfileDocuments('identity'),
        documents: [],
        show: !kycProviderExternal.value && computed(() => isVisibleNaturalProfileDocuments('identity')),
        subheadline: i18n.global.t('verification.identity_verification_subheadline'),
    },
    kyc_tax: {
        header: i18n.global.t('verification.tax_verification'),
        icon: ReceiptTaxIcon,
        accepted: computed(() => naturalProfile.kyc_tax),
        required: false,
        documents: [],
        show: computed(() => isVisibleNaturalProfileDocuments('tax')),
        subheadline: i18n.global.t('verification.tax_verification_headline'),
    },
    kyc_bank: {
        header: i18n.global.t('verification.bank_verification'),
        accepted: computed(() => naturalProfile.kyc_bank),
        required: isRequiredNaturalProfileDocuments('bank'),
        icon: CashIcon,
        show: computed(() => isVisibleNaturalProfileDocuments('bank')),
        subheadline: i18n.global.t('verification.bank_verification_headline'),
        documents: [],
    },
    kyc_address: {
        header: i18n.global.t('verification.address_verification'),
        accepted: computed(() => naturalProfile.kyc_address),
        required: isRequiredNaturalProfileDocuments('address'),
        icon: LocationMarkerIcon,
        documents: [],
        headline: i18n.global.t('verification.address_verification_headline'),
        show: computed(() => isVisibleNaturalProfileDocuments('address')),
        subheadline: i18n.global.t('verification.address_verification_subheadline'),
    },
})

// if no tab is provided select kyc_id
const currentTabKey = ref<keyof KycDocumentTabs>(
    navigationStore.tab ??
        Object.keys(kycDocuments).filter((d) => {
            return kycDocuments[d].show ? d : false
        })[0]
)
watch(currentTabKey, (val) => (navigationStore.tab = val))

const bankAccountHeaders: DataTableHeader[] = [
    {
        text: i18n.global.t('bank_account.holder'),
        value: 'holder',
    },
    {
        text: i18n.global.t('bank_account.iban'),
        value: 'iban',
    },
    {
        text: i18n.global.t('bank_account.status'),
        value: 'status',
    },
    {
        text: i18n.global.t('bank_account.is_main'),
        value: 'is_main',
        classes: 'text-right',
    },
]

function setAsMainBankAccount(bankAccount: FrontendBankAccountResource) {
    putBankAccountUpdate(bankAccount.id, { is_main: true })
        .then(() => {
            notifySuccess(i18n.global.t('bank_account.set_as_main_bank_account_success_message'))
            store.fetchKycDocuments()
            store.fetchBankAccounts()
        })
        .catch((error) => {
            notifyError(error.response?.data?.errorMessage)
        })
}
</script>

<template>
    <AccountKycProcessTabs v-model:tab-key="currentTabKey" :kyc-document-tabs="kycDocuments" :account-store="store">
        <template #additional-header-content>
            <div v-if="currentTabKey === 'kyc_bank'">
                <h3 class="mb-2 mt-6 text-lg font-medium leading-6 text-gray-900">
                    {{ i18n.global.t('bank_account.bank_accounts') }}
                </h3>
                <div class="overflow-hidden rounded-md border border-gray-200">
                    <BaseTable
                        v-if="store.bankAccounts.length > 0"
                        :items="store.bankAccounts"
                        :headers="bankAccountHeaders">
                        <template #item.status="{ value }">
                            <BankAccountStatusIndicator :status="value" />
                        </template>
                        <template #item.is_main="{ item }">
                            <div>
                                <small v-if="item.is_main" class="flex justify-end text-sm text-blue-800">
                                    {{ $t('bank_account.main_bank_account') }}
                                    <StarIcon class="ml-1 w-4" />
                                </small>
                                <a
                                    v-else-if="item.status === BankAccountStatus.VERIFIED"
                                    class="cursor-pointer text-right text-sm text-gray-900"
                                    @click="setAsMainBankAccount(item)">
                                    {{ $t('bank_account.set_as_main_bank_account') }}
                                </a>
                            </div>
                        </template>
                    </BaseTable>
                    <EmptyState
                        v-else
                        classes="my-4"
                        :header="$t('bank_account.empty_state_header')"
                        :description="$t('bank_account.empty_state_description')" />
                </div>
            </div>
        </template>
        <template #body>
            <div v-if="currentTabKey === 'kyc_external'">
                <AccountKycProcessExternal />
            </div>
        </template>
    </AccountKycProcessTabs>
</template>
